/**
 * URL parameter extraction utilities for referral code handling
 *
 * This module provides functions to extract affiliate tokens from URLs,
 * supporting both legacy and new URL formats for the registration flow.
 */

/**
 * Extracts the affiliate_token parameter from a URL string
 *
 * Supports two URL formats:
 * - Legacy: /register/new?affiliate_token=...
 * - New: /public/register?step=start&affiliate_token=...
 *
 * @param url - The URL string to extract the token from
 * @returns The affiliate token if found, null otherwise
 *
 * @example
 * // Legacy format
 * extractAffiliateToken('https://example.com/register/new?affiliate_token=ABC123')
 * // returns: 'ABC123'
 *
 * @example
 * // New format
 * extractAffiliateToken('https://example.com/public/register?step=start&affiliate_token=XYZ789')
 * // returns: 'XYZ789'
 *
 * @example
 * // No token present
 * extractAffiliateToken('https://example.com/public/register')
 * // returns: null
 */
export function extractAffiliateToken(url: string): string | null {
  try {
    // Validate that the URL is a valid absolute URL
    // We need to handle full URLs only, not relative paths
    if (!url || typeof url !== "string") {
      return null;
    }

    // Check if it's a valid URL format
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      return null;
    }

    // Parse the URL to extract query parameters
    const urlObj = new URL(url);

    // Get the affiliate_token parameter value
    const affiliateToken = urlObj.searchParams.get("affiliate_token");

    // Return null if token is empty or not present
    if (!affiliateToken || affiliateToken.trim() === "") {
      return null;
    }

    // Return the token value (URL decoding is handled automatically by URLSearchParams)
    // No client-side validation is performed - backend will validate the token
    return affiliateToken;
  } catch (error) {
    // Handle any parsing errors gracefully
    // This could happen with malformed URLs
    console.warn("Failed to extract affiliate token from URL:", error);
    return null;
  }
}
